// src/utils/appointmentDataNormalizer.js

/**
 * Normalize intent field names from frontend to match backend schema
 * Converts: purposeType -> purpose, occasionType -> occasion
 */
export const normalizeIntentForBackend = (intent) => {
  if (!intent) return intent;

  const normalized = { ...intent };

  // Map purposeType to purpose
  if (intent.purposeType !== undefined) {
    normalized.purpose = intent.purposeType;
    delete normalized.purposeType;
  }

  // Map occasionType to occasion
  if (intent.occasionType !== undefined) {
    normalized.occasion = intent.occasionType;
    delete normalized.occasionType;
  }

  // Ensure occasionDate is included
  if (intent.occasionDate !== undefined) {
    normalized.occasionDate = intent.occasionDate;
  }

  // Ensure forWhom is included
  if (intent.forWhom !== undefined) {
    normalized.forWhom = intent.forWhom;
  }

  // Clean up empty strings to null for optional fields
  if (normalized.occasion === "") {
    normalized.occasion = null;
  }

  if (normalized.occasionDate === "") {
    normalized.occasionDate = null;
  }

  if (normalized.forWhom === "") {
    normalized.forWhom = null;
  }

  return normalized;
};

/**
 * Normalize intent from backend to frontend format
 * Converts: purpose -> purposeType, occasion -> occasionType
 */
export const normalizeIntentFromBackend = (intent) => {
  if (!intent) return intent;

  return {
    ...intent,
    purposeType: intent.purpose,
    occasionType: intent.occasion,
  };
};

/**
 * Validate and clean metadata updates
 * Ensures all fields are properly typed before sending
 */
export const normalizeMetadataForBackend = (metadata) => {
  if (!metadata) return metadata;

  const normalized = {};

  // Only include defined values
  Object.keys(metadata).forEach((key) => {
    const value = metadata[key];

    // Skip undefined values
    if (value === undefined) {
      return;
    }

    // Handle tags specially (ensure it's an array)
    if (key === "tags") {
      normalized[key] = Array.isArray(value) ? value : [];
      return;
    }

    // Include the value
    normalized[key] = value;
  });

  return normalized;
};

/**
 * Clean empty/undefined values from an object
 */
export const cleanEmptyValues = (obj) => {
  if (!obj || typeof obj !== "object") return obj;

  const cleaned = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    // Skip undefined
    if (value === undefined) {
      return;
    }

    // Convert empty strings to null for optional fields
    if (value === "") {
      cleaned[key] = null;
      return;
    }

    // Recursively clean nested objects
    if (value && typeof value === "object" && !Array.isArray(value)) {
      cleaned[key] = cleanEmptyValues(value);
      return;
    }

    // Keep the value
    cleaned[key] = value;
  });

  return cleaned;
};
